import { useEffect, useState } from 'react'
import { RecentCallPicker } from '@/features/phone-calls/recent-call-picker'
import { toast } from 'sonner'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/loading-button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AlertCircle, MessageSquare, Phone } from 'lucide-react'
import { formatApiErrorMessage } from '@/lib/api-error'
import {
  useRecordCommunication,
  useCreatePOThread,
  useDefaultPORecipients,
} from '@/features/communications/hooks'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ComposeInput, ComposeTextarea } from '@/components/compose-field'

export function RecordCommunicationInline({ poUuid, poId }: { poUuid: string; poId?: number }) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [source, setSource] = useState('phone_call')
  const [callPicked, setCallPicked] = useState(false)
  const mutation = useRecordCommunication(poUuid)

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Phone className="mr-1 h-3 w-3" /> Record Communication
      </Button>
    )
  }

  return (
    <Card className="w-full">
      <CardContent className="space-y-3 pt-4">
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="phone_call">Phone Call</SelectItem>
            <SelectItem value="in_person">In Person</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
        {source === 'phone_call' && poId !== undefined && (
          <RecentCallPicker
            target={{ purchase_order_id: poId }}
            onPickedChange={setCallPicked}
            onLinked={() => {
              setCallPicked(false)
              setOpen(false)
            }}
          />
        )}
        {!callPicked && (
        <>
        <ComposeTextarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Summarize what the manufacturer communicated..."
          rows={4}
          className="text-sm"
        />
        <div className="flex gap-2">
          <LoadingButton
            size="sm"
            loading={mutation.isPending}
            loadingText="Recording..."
            onClick={() => {
              mutation.mutate(
                { body_text: text, source },
                {
                  onSuccess: () => {
                    setText('')
                    setOpen(false)
                  },
                },
              )
            }}
            disabled={!text.trim()}
          >
            <Phone className="mr-1 h-3 w-3" /> Record
          </LoadingButton>
          <Button size="sm" variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </div>
        </>
        )}
      </CardContent>
    </Card>
  )
}

type NewThreadTemplate = 'po_email' | 'spec_credit' | 'blank'

export function NewThreadDialog({
  poUuid,
  hasPoPdf,
  hasQuotePdf,
}: {
  poUuid: string
  hasPoPdf: boolean
  hasQuotePdf: boolean
}) {
  const [open, setOpen] = useState(false)
  const [template, setTemplate] = useState<NewThreadTemplate>('blank')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [ccEmails, setCcEmails] = useState('')
  const [subject, setSubject] = useState('')
  const [bodyText, setBodyText] = useState('')
  const [attachPoPdf, setAttachPoPdf] = useState(false)
  const [attachQuotePdf, setAttachQuotePdf] = useState(false)
  // Load defaults lazily — only after dialog opens. Refetch per template so
  // spec_credit drafts default to the spec credit contact (and PO drafts to
  // the PO contact).
  const { data: defaults, isLoading: defaultsLoading } = useDefaultPORecipients(
    poUuid,
    open,
    template,
  )
  const createMutation = useCreatePOThread(poUuid)

  // Pre-fill defaults once they arrive (but only if user hasn't already typed).
  useEffect(() => {
    if (!open || !defaults) return
    if (!recipientEmail && defaults.email) setRecipientEmail(defaults.email)
    if (!ccEmails && defaults.cc_emails && defaults.cc_emails.length > 0) {
      setCcEmails(defaults.cc_emails.join(', '))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaults, open])

  // Adjust default attachment flags when template changes. Also clear the
  // recipient/cc fields so the new template's defaults can re-prefill (the
  // user can still edit afterward).
  useEffect(() => {
    if (template === 'po_email') {
      setAttachPoPdf(hasPoPdf)
      setAttachQuotePdf(false)
    } else if (template === 'spec_credit') {
      setAttachPoPdf(false)
      setAttachQuotePdf(hasQuotePdf)
    } else {
      setAttachPoPdf(false)
      setAttachQuotePdf(false)
    }
    setRecipientEmail('')
    setCcEmails('')
  }, [template, hasPoPdf, hasQuotePdf])

  const reset = () => {
    createMutation.reset()
    setTemplate('blank')
    setRecipientEmail('')
    setCcEmails('')
    setSubject('')
    setBodyText('')
    setAttachPoPdf(false)
    setAttachQuotePdf(false)
  }

  const handleSubmit = () => {
    const cc = ccEmails
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    createMutation.mutate(
      {
        template,
        subject: subject.trim() || undefined,
        recipient_email: recipientEmail.trim() || undefined,
        cc_emails: cc,
        body_text: bodyText.trim() || undefined,
        attach_po_pdf: attachPoPdf,
        attach_quote_pdf: attachQuotePdf,
      },
      {
        onSuccess: () => {
          toast.success('Thread created')
          setOpen(false)
          reset()
        },
      },
    )
  }

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <MessageSquare className="mr-1 h-3 w-3" /> New Thread
      </Button>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o)
          if (!o) reset()
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>New communication thread</DialogTitle>
            <DialogDescription>
              Defaults to the manufacturer's PO contacts — edit them as needed. Once created, every
              reply in this thread uses the TO/CC you set here.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Template</label>
              <Select value={template} onValueChange={(v) => setTemplate(v as NewThreadTemplate)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="blank">Blank</SelectItem>
                  <SelectItem value="po_email">Purchase Order Email</SelectItem>
                  <SelectItem value="spec_credit">Spec Credit Request</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                To {defaultsLoading && <span className="italic">— loading defaults...</span>}
              </label>
              <ComposeInput
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="vendor@example.com"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                CC (comma-separated)
              </label>
              <ComposeInput
                value={ccEmails}
                onChange={(e) => setCcEmails(e.target.value)}
                placeholder="cc1@example.com, cc2@example.com"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Subject (optional)</label>
              <ComposeInput
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={
                  template === 'po_email'
                    ? 'Auto-generated from PO number'
                    : template === 'spec_credit'
                      ? 'Auto-generated spec credit subject'
                      : 'PO ' + poUuid.slice(0, 8)
                }
              />
            </div>
            {template === 'blank' && (
              <div>
                <label className="text-xs font-medium text-muted-foreground">Body (optional)</label>
                <ComposeTextarea
                  value={bodyText}
                  onChange={(e) => setBodyText(e.target.value)}
                  placeholder="Leave blank to create an empty draft to edit after the thread is created."
                  rows={6}
                />
              </div>
            )}
            {template === 'po_email' && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={attachPoPdf}
                  onChange={(e) => setAttachPoPdf(e.target.checked)}
                  disabled={!hasPoPdf}
                />
                Attach PO PDF {!hasPoPdf && <span className="text-muted-foreground">(not available)</span>}
              </label>
            )}
            {template === 'spec_credit' && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={attachQuotePdf}
                  onChange={(e) => setAttachQuotePdf(e.target.checked)}
                  disabled={!hasQuotePdf}
                />
                Attach Quote PDF{' '}
                {!hasQuotePdf && <span className="text-muted-foreground">(not available)</span>}
              </label>
            )}
          </div>
          {createMutation.isError && (
            <div
              role="alert"
              className="flex gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              <p>{formatApiErrorMessage('Could not create thread', createMutation.error)}</p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setOpen(false)
                reset()
              }}
            >
              Cancel
            </Button>
            <LoadingButton
              loading={createMutation.isPending}
              loadingText="Creating..."
              onClick={handleSubmit}
              disabled={!recipientEmail.trim()}
            >
              <MessageSquare className="mr-1 h-3 w-3" /> Create thread
            </LoadingButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
